/**
 * Card Color Swatches
 * Handles color swatch clicks on product cards in collection pages
 * Changes the product image when a different color is selected
 */

class CardColorSwatches {
  constructor() {
    this.init();
  }

  init() {
    document.addEventListener('click', this.handleSwatchClick.bind(this));
  }

  handleSwatchClick(event) {
    const swatch = event.target.closest('.card-color-swatch:not(.card-color-swatch--more)');
    if (!swatch) return;

    event.preventDefault();
    event.stopPropagation();

    const swatchContainer = swatch.closest('.card-color-swatches');
    if (!swatchContainer) return;

    const card = swatch.closest('.card-wrapper');
    if (!card) return;

    // Update active state
    this.updateActiveState(swatchContainer, swatch);

    // Update product image
    const variantImage = swatch.dataset.variantImage;
    if (variantImage) {
      this.updateProductImage(card, variantImage, swatch.dataset.color);
    }

    // Update product link
    const variantUrl = swatch.dataset.variantUrl;
    if (variantUrl) {
      this.updateProductLinks(card, variantUrl);
    }
  }

  updateActiveState(container, activeSwatch) {
    // Remove active class from all swatches
    container.querySelectorAll('.card-color-swatch').forEach(swatch => {
      swatch.classList.remove('card-color-swatch--active');
    });

    // Add active class to clicked swatch
    activeSwatch.classList.add('card-color-swatch--active');
  }

  updateProductImage(card, newImageUrl, colorName) {
    const mediaContainer = card.querySelector('.card__media');
    if (!mediaContainer) return;

    const primaryImage = mediaContainer.querySelector('.media > img:first-child');
    if (!primaryImage) return;

    // Add transitioning class for smooth animation
    mediaContainer.classList.add('card__media--color-transitioning');

    // Create new image srcset based on the new image URL
    const baseUrl = newImageUrl.split('?')[0];
    const newSrcset = this.generateSrcset(baseUrl);

    // Preload the new image
    const preloadImage = new Image();
    preloadImage.onload = () => {
      // Update the primary image
      primaryImage.srcset = newSrcset;
      primaryImage.src = newImageUrl;
      primaryImage.alt = colorName || primaryImage.alt;

      // Remove transitioning class after image loads
      setTimeout(() => {
        mediaContainer.classList.remove('card__media--color-transitioning');
      }, 50);
    };

    preloadImage.onerror = () => {
      mediaContainer.classList.remove('card__media--color-transitioning');
    };

    preloadImage.src = newImageUrl;
  }

  generateSrcset(baseUrl) {
    const widths = [165, 360, 533, 720, 940, 1066];
    const srcsetParts = [];

    widths.forEach(width => {
      // Extract base URL and add width parameter
      const urlWithWidth = this.addWidthToUrl(baseUrl, width);
      srcsetParts.push(`${urlWithWidth} ${width}w`);
    });

    return srcsetParts.join(', ');
  }

  addWidthToUrl(url, width) {
    // Handle Shopify CDN URLs
    if (url.includes('cdn.shopify.com')) {
      // Check if URL already has parameters
      if (url.includes('?')) {
        return url.replace(/width=\d+/, `width=${width}`);
      } else {
        // For URLs like //cdn.shopify.com/s/files/1/xxx/files/image.jpg
        // Need to add width parameter
        const parts = url.split('?');
        return `${parts[0]}?width=${width}`;
      }
    }
    return url;
  }

  updateProductLinks(card, variantUrl) {
    // Update all product links in the card to point to the variant
    const productLinks = card.querySelectorAll('a[href*="/products/"]');
    productLinks.forEach(link => {
      // Only update links that go to the same product
      const currentUrl = new URL(link.href, window.location.origin);
      const newUrl = new URL(variantUrl, window.location.origin);

      if (currentUrl.pathname === newUrl.pathname.split('?')[0] ||
          currentUrl.pathname.includes('/products/')) {
        // Preserve the pathname but update the variant
        const basePathname = currentUrl.pathname.split('?')[0];
        link.href = basePathname + '?variant=' + new URLSearchParams(newUrl.search).get('variant');
      }
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new CardColorSwatches());
} else {
  new CardColorSwatches();
}
